'use client'
import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ArrowTrendingUpIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import Button from '@/components/Button'
import ServicesData from '@/data/services.json'

const StartToServices = () => {
  const { services } = ServicesData
  const topServices = services.sort((a, b) => a.order - b.order).slice(0, 3)

  const containerRef = useRef(null)
  const barsRef = useRef(null)
  const diagonalLineRef = useRef(null)

  // Left Column Refs
  const iconBadgeRef = useRef(null)
  const headline1Ref = useRef(null)
  const headlineLegacyRef = useRef(null)
  const headline2Ref = useRef(null)
  const missionCardRef = useRef(null)
  const ctaRef = useRef(null)
  const leftColumnRef = useRef(null)

  // Right Column Refs
  const rightColumnRef = useRef(null)
  const glassLayer1Ref = useRef(null)
  const glassLayer2Ref = useRef(null)
  const glassLayer3Ref = useRef(null)

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion) {
      // Skip animations, show final states
      gsap.set([iconBadgeRef.current, headline1Ref.current, headlineLegacyRef.current, headline2Ref.current, missionCardRef.current, ctaRef.current], { autoAlpha: 1, x: 0, y: 0, scale: 1 })
      gsap.set([glassLayer1Ref.current, glassLayer2Ref.current, glassLayer3Ref.current], { autoAlpha: 1, x: 0 })
      gsap.set(diagonalLineRef.current, { scaleY: 1 })
      if (barsRef.current) {
        const bars = barsRef.current.querySelectorAll('span')
        gsap.set(bars, { scaleY: 1 })
      }
      // Mobile cards
      const mobileCards = document.querySelectorAll('.mobile-service-card')
      gsap.set(mobileCards, { autoAlpha: 1, y: 0 })
      return
    }

    const ctx = gsap.context(() => {
      gsap.registerPlugin(ScrollTrigger)

      // --- INITIAL STATES ---
      gsap.set(iconBadgeRef.current, { scale: 0.5, autoAlpha: 0 })
      gsap.set([headline1Ref.current, headline2Ref.current], { x: -100, autoAlpha: 0 })
      gsap.set(headlineLegacyRef.current, { x: -100, scale: 0.9, autoAlpha: 0 })
      gsap.set(missionCardRef.current, { y: 40, autoAlpha: 0 })
      gsap.set(ctaRef.current, { y: 20, autoAlpha: 0 })
      gsap.set([glassLayer1Ref.current, glassLayer2Ref.current, glassLayer3Ref.current], { x: 100, autoAlpha: 0 })
      gsap.set(diagonalLineRef.current, { scaleY: 0, transformOrigin: 'top' })

      // --- EMPIRE PILLARS ANIMATION ---
      if (barsRef.current) {
        const bars = barsRef.current.querySelectorAll('span')
        gsap.fromTo(
          bars,
          { scaleY: 0, transformOrigin: 'bottom' },
          {
            scaleY: 1,
            duration: 1.2,
            ease: 'power4.out',
            stagger: {
              amount: 0.6,
              from: 'end', // Stagger from right to left (opposite of Hero)
            },
          }
        )
      }

      // --- DIAGONAL GOLD LINE ---
      gsap.to(diagonalLineRef.current, {
        scaleY: 1,
        duration: 1.5,
        ease: 'power2.inOut',
      })

      // --- MAIN CONTENT TIMELINE ---
      const tl = gsap.timeline({ delay: 0.8 })

      tl.to(iconBadgeRef.current, {
        scale: 1,
        autoAlpha: 1,
        duration: 0.8,
        ease: 'back.out(1.5)',
      })
        .to(headline1Ref.current, {
          x: 0,
          autoAlpha: 1,
          duration: 0.8,
          ease: 'power3.out',
        }, '-=0.4')
        .to(headlineLegacyRef.current, {
          x: 0,
          scale: 1,
          autoAlpha: 1,
          duration: 1.2,
          ease: 'power3.out',
        }, '-=0.6')
        .to(headline2Ref.current, {
          x: 0,
          autoAlpha: 1,
          duration: 0.8,
          ease: 'power3.out',
        }, '-=0.8')
        .to(missionCardRef.current, {
          y: 0,
          autoAlpha: 1,
          duration: 1,
          ease: 'power2.out',
        }, '-=0.4')
        .to(ctaRef.current, {
          y: 0,
          autoAlpha: 1,
          duration: 0.6,
          ease: 'back.out(1.7)',
        }, '-=0.2')

      // --- RIGHT COLUMN GLASS LAYERS (Sequential Stagger) ---
      tl.to([glassLayer1Ref.current, glassLayer2Ref.current, glassLayer3Ref.current], {
        x: 0,
        autoAlpha: 1,
        duration: 1.05,
        ease: 'power3.out',
        stagger: 0.12,
      }, '-=0.75')

      // --- PARALLAX SCROLL EFFECT (Desktop only) ---
      const mm = gsap.matchMedia()

      mm.add('(min-width: 1070px)', () => {
        // Parallax for left column (slower movement)
        gsap.to(leftColumnRef.current, {
          y: '-5%',
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.5,
          },
        })

        // Parallax for right column (faster movement for depth)
        gsap.to(rightColumnRef.current, {
          y: '15%',
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.5,
          },
        })
      })

      // --- MOBILE CARDS ANIMATION (Below lg-first) ---
      mm.add('(max-width: 1069px)', () => {
        const mobileCards = gsap.utils.toArray('.mobile-service-card')

        if (mobileCards.length > 0) {
          gsap.set(mobileCards, { y: 60, opacity: 0 })

          gsap.to(mobileCards, {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.2,
            scrollTrigger: {
              trigger: '.mobile-cards-container',
              start: 'top 80%',
              toggleActions: 'play none none none',
              once: true,
            },
          })
        }
      })
    }, containerRef)

    return () => ctx.revert()
  }, [])

  // SVG Icon Renderer Helper
  const renderIcon = (iconString) => {
    if (!iconString) return null
    return (
      <div
        dangerouslySetInnerHTML={{ __html: iconString }}
        className="w-8 h-8 sm:w-10 sm:h-10 [&>svg]:w-full [&>svg]:h-full [&>svg]:fill-secondary"
      />
    )
  }

  return (
    <div
      ref={containerRef}
      id="mission"
      className="relative min-h-screen w-full bg-primary overflow-hidden grid grid-cols-1 lg-first:grid-cols-[50%_50%] min-[1575px]:grid-cols-[55%_45%] gap-0"
    >
      {/* --- BACKGROUND LAYERS --- */}
      {/* Multi-layer Radial Gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-gray-900 via-black to-black z-0 pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent z-0 pointer-events-none"></div>

      {/* Subtle Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.02] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] z-0 pointer-events-none mix-blend-overlay"></div>

      {/* --- EMPIRE PILLARS (9 bars, asymmetric) --- */}
      <div
        ref={barsRef}
        className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 sm:px-10 h-[60%] max-lg-first:h-[40%] z-0 opacity-15 max-lg-first:opacity-10 pointer-events-none gap-2 sm:gap-4"
      >
        {[0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 0.95, 0.85, 0.75].map((h, i) => (
          <span
            key={i}
            className="w-full rounded-t-sm border-t border-secondary/30 bg-gradient-to-b from-gray-500 via-black to-black"
            style={{ height: `${h * 100}%` }}
          />
        ))}
      </div>

      {/* --- DIAGONAL GOLD LINE DIVIDER (Desktop only) --- */}
      <div
        ref={diagonalLineRef}
        className="hidden lg-first:block absolute top-0 right-[50%] w-px h-full bg-gradient-to-b from-transparent via-secondary/30 to-transparent z-0 pointer-events-none"
      ></div>

      {/* --- LEFT COLUMN: TEXT CONTENT --- */}
      <div
        ref={leftColumnRef}
        className="relative flex flex-col justify-center px-6 sm:px-12 lg-first:px-12 min-[1575px]:px-16 py-16 lg-first:py-24 z-10"
      >
        {/* Icon Badge */}
        <div
          ref={iconBadgeRef}
          className="relative w-16 h-16 sm:w-20 sm:h-20 mb-6 lg-first:mb-8"
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-secondary/20 rounded-full blur-xl"></div>
          <div className="relative w-full h-full text-secondary flex items-center justify-center">
            <ArrowTrendingUpIcon className="w-10 h-10 sm:w-12 sm:h-12 drop-shadow-[0_0_20px_rgba(223,175,55,0.6)]" />
          </div>
        </div>

        {/* Oversized Headline */}
        <h2 className="font-montserrat font-bold text-6xl sm:text-7xl md:text-8xl lg-first:text-6xl min-[1575px]:text-9xl leading-[0.95] text-left mb-6 lg-first:mb-8">
          <div ref={headline1Ref} className="text-white mb-2">
            Your Financial
          </div>
          <div
            ref={headlineLegacyRef}
            className="empire-text !text-left text-5xl sm:text-7xl md:text-8xl lg-first:text-8xl min-[1575px]:text-9xl font-black tracking-wider my-4 lg-first:my-6"
          >
            LEGACY
          </div>
          <div ref={headline2Ref} className="text-white">
            Starts Here
          </div>
        </h2>

        {/* Mission Statement - Glass Card */}
        <div
          ref={missionCardRef}
          className="glass-card rounded-xl p-6 sm:p-8 border-l-4 border-secondary max-w-2xl"
        >
          <p className="text-base sm:text-lg lg-first:text-lg min-[1575px]:text-xl text-gray-200 leading-relaxed">
            From basement to penthouse, financial freedom isn't a dream — it's a{' '}
            <span className="text-secondary font-medium">decision</span>. At Legacy
            Asset Leaders, our mission is to help individuals, families, and
            entrepreneurs master{' '}
            <span className="text-secondary font-medium">financial literacy</span>,
            protect wealth, and build{' '}
            <span className="text-secondary font-medium">lasting legacies</span>.
          </p>
        </div>

        {/* CTA Button */}
        <div ref={ctaRef} className="mt-8 lg-first:mt-10">
          <a href="#contact" className="inline-block group">
            <Button
              variant="bg-custom"
              className="empire-button relative flex items-center px-8 py-4 text-sm font-bold uppercase tracking-widest rounded-full gap-3 text-black bg-gradient-to-r from-secondary via-[#F4E080] to-secondary shadow-xl hover:scale-105 transition-transform duration-300"
            >
              <span className="relative z-10 flex items-center gap-2">
                Discover Our Services
                <ArrowRightIcon className="w-4 h-4" />
              </span>
            </Button>
          </a>
        </div>
      </div>

      {/* --- MOBILE: SERVICE CARDS STACK (Below lg-first breakpoint) --- */}
      <div className="relative lg-first:hidden w-full px-6 sm:px-12 pb-16 z-10 mobile-cards-container">
        <h2 className="sr-only">Our Services Highlights</h2>
        <div className="flex flex-col gap-6 max-w-lg mx-auto">
          {topServices.map((service) => (
            <div
              key={service.id}
              className="glass-card mobile-service-card rounded-2xl border-2 border-secondary/30 shadow-xl p-6 flex flex-col"
              role="article"
              aria-labelledby={`service-${service.id}-title`}
            >
              {/* Icon */}
              <div className="mb-4">
                {renderIcon(service.icon)}
              </div>

              {/* Title */}
              <h3 id={`service-${service.id}-title`} className="text-white font-bold text-xl mb-2 leading-tight">
                {service.title}
              </h3>

              {/* Tagline */}
              <p className="text-secondary/90 text-base mb-3 font-medium leading-snug">
                {service.second_title}
              </p>

              {/* Full Description on Mobile */}
              <p className="text-gray-300 text-sm leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* --- RIGHT COLUMN: FLOATING GLASS LAYERS VISUAL --- */}
      <div
        ref={rightColumnRef}
        className="relative hidden lg-first:flex items-center justify-center p-8 lg-first:p-12 min-[1575px]:p-16 z-10 overflow-visible"
      >
        <div className="relative w-full max-w-2xl min-h-[750px] max-xlg:min-h-[780px] min-[1665px]:min-h-[700px] overflow-visible">
          {/* Glass Layer 1 - Legacy Consultation (Largest - Hero Card) */}
          <div
            ref={glassLayer1Ref}
            className="glass-card-premium !absolute top-[-10%] left-[-2%] max-xlg:left-[-8%] max-xlg:top-[-9%] max-xl:left-[-16%] w-[17.8125rem] min-[1420px]:w-80 max-[1420px]:h-[375px] rounded-2xl transform rotate-[-4deg] shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_40px_rgba(223,175,55,0.15),inset_0_1px_1px_rgba(255,255,255,0.15)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.5),0_0_60px_rgba(223,175,55,0.3)] p-8 min-[1420px]:p-9 flex flex-col justify-between z-[15] transition-all duration-500 hover:scale-[1.03] hover:rotate-[-1deg] hover:z-50 cursor-pointer group border border-secondary/20"
            role="article"
            aria-labelledby="service-1-title"
          >
            {/* Frosted Edge Highlights */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent rounded-t-2xl"></div>
            <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-white/30 via-transparent to-transparent rounded-l-2xl"></div>
            
            {/* Hover Glow Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/10 group-hover:to-transparent transition-all duration-500 rounded-2xl pointer-events-none"></div>

            {/* Icon */}
            <div className="relative z-10 mb-5">
              {renderIcon(topServices[0]?.icon)}
            </div>

            {/* Title */}
            <h3 id="service-1-title" className="relative z-10 text-white font-bold text-xl mb-3 leading-tight">
              {topServices[0]?.title}
            </h3>

            {/* Tagline */}
            <p className="relative z-10 text-secondary/90 text-base mb-4 font-medium leading-snug">
              {topServices[0]?.second_title}
            </p>

            {/* Description */}
            <p className="relative z-10 text-gray-300 text-sm leading-relaxed">
              {topServices[0]?.description}
            </p>
          </div>

          {/* Glass Layer 2 - Legacy Life Blueprint (Medium - Featured Card) */}
          <div
            ref={glassLayer2Ref}
            className="glass-card-premium !absolute top-[23%] right-[-4%] w-[17.8125rem] min-[1420px]:w-80 max-[1420px]:h-[375px] rounded-2xl transform rotate-[6deg] shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_40px_rgba(223,175,55,0.15),inset_0_1px_1px_rgba(255,255,255,0.15)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.5),0_0_60px_rgba(223,175,55,0.3)] p-8 min-[1420px]:p-9 flex flex-col justify-between z-[20] transition-all duration-500 hover:scale-[1.03] hover:rotate-[2deg] hover:z-50 cursor-pointer group border border-secondary/30"
            role="article"
            aria-labelledby="service-2-title"
          >
            {/* Frosted Edge Highlights */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent rounded-t-2xl"></div>
            <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-white/30 via-transparent to-transparent rounded-r-2xl"></div>
            
            {/* Hover Glow Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/10 group-hover:to-transparent transition-all duration-500 rounded-2xl pointer-events-none"></div>

            {/* Icon */}
            <div className="relative z-10 mb-5">
              {renderIcon(topServices[1]?.icon)}
            </div>

            {/* Title */}
            <h3 id="service-2-title" className="relative z-10 text-white font-bold text-xl mb-3 leading-tight">
              {topServices[1]?.title}
            </h3>

            {/* Tagline */}
            <p className="relative z-10 text-secondary/90 text-base mb-4 font-medium leading-snug">
              {topServices[1]?.second_title}
            </p>

            {/* Description */}
            <p className="relative z-10 text-gray-300 text-sm leading-relaxed">
              {topServices[1]?.description}
            </p>
          </div>

          {/* Glass Layer 3 - Legacy Capital Empowerment (Smaller - Supporting Card) */}
          <div
            ref={glassLayer3Ref}
            className="glass-card-premium !absolute top-[47%] left-[-2%] max-xlg:left-[-8%] max-xlg:top-[47%] max-xl:left-[-16%] w-[17.8125rem] min-[1420px]:w-80  max-[1420px]:h-[375px] rounded-2xl transform rotate-[-5deg] shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_40px_rgba(223,175,55,0.15),inset_0_1px_1px_rgba(255,255,255,0.15)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.5),0_0_60px_rgba(223,175,55,0.3)] z-[10] p-8 min-[1420px]:p-9 flex flex-col justify-between transition-all duration-500 hover:scale-[1.03] hover:rotate-[-1deg] hover:z-50 cursor-pointer group border border-secondary/20"
            role="article"
            aria-labelledby="service-3-title"
          >
            {/* Frosted Edge Highlights */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent rounded-t-2xl"></div>
            <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-white/30 via-transparent to-transparent rounded-l-2xl"></div>
            
            {/* Hover Glow Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/10 group-hover:to-transparent transition-all duration-500 rounded-2xl pointer-events-none"></div>

            {/* Icon */}
            <div className="relative z-10 mb-5">
              {renderIcon(topServices[2]?.icon)}
            </div>

            {/* Title */}
            <h3 id="service-3-title" className="relative z-10 text-white font-bold text-xl mb-3 leading-tight">
              {topServices[2]?.title}
            </h3>

            {/* Tagline */}
            <p className="relative z-10 text-secondary/90 text-base mb-4 font-medium leading-snug">
              {topServices[2]?.second_title}
            </p>

            {/* Description */}
            <p className="relative z-10 text-gray-300 text-sm leading-relaxed">
              {topServices[2]?.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StartToServices
